/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
import {
  removeFromArray,
  toLatLng,
  objectifyByKey,
  cloneObject,
  clearSearchString,
  filterDuplicatesFromArray,
} from '@/helpers/utility';
import { intToTimeStr, timeLocalFormat } from '@/helpers/dateTime';
import { distinctColors } from '@/helpers/constants';
// import { decode } from '@/helpers/here/flexipolyline';
import Vue from 'vue';
import { orderStations, decodePolyline } from '../../components/routeList/utils';
import workgroupService from '../../services/workgroup';
import routeListService from '../../services/routeList';
import { getPersonnelId } from '../utils/personnelStationUtils';

function routesFromActive(payload) {
  return payload.map((el, index) => {
    // Always 'routePath' today; kept as a field on the route object for forward compatibility
    // with potential future path types (e.g. return path).
    const pathType = 'routePath';
    const stations = orderStations(el.route[pathType]?.stationIds, el.stations);
    const isGhost = !el.route?.routePath?.encodedPolyline;
    return {
      attendingPersonnelCount: el.route.attendingPersonnelCount,
      avgTimeCoefficient: el.route.avgTimeCoefficient,
      avgWalkingDistanceInMeter: el.route.avgWalkingDistanceInMeter,
      driver: `${el.route.driver.name ? el.route.driver.name : ''} ${
        el.route.driver.surname ? el.route.driver.surname : ''
      }`,
      driverId: el.route.driver.id,
      driverPersonnelId: el.route.driver.personnelId,
      encodedPolyline: el.route.routePath.encodedPolyline,
      endTime: intToTimeStr(el.route.endTime),
      id: el.route.id,
      isGhost,
      isActive: el.route.isActive,
      isTransferApplied: el.route.isTransferApplied,
      isEditMode: false,
      name: el.route.name,
      personnelCount: el.route.personnelCount,
      periodStart: el.route.periodStart,
      plate: el.route.vehicle.plateId,
      position: el?.route[pathType]?.encodedPolyline ? decodePolyline(el.route[pathType].encodedPolyline) : [],
      reservationCount: el.route.reservationCount,
      routeColor: el.route?.color || distinctColors[index % distinctColors.length],
      routeId: el.route.id,
      routeName: el.route.name,
      showRoute: true,
      startTime: intToTimeStr(el.route.startTime),
      status: el.route.vehicle.deviceStatus || '-',
      title: el.route.title,
      totalCost: el.route.totalCost,
      totalDistance: el.route.totalKm,
      totalDuration: el.route.durationInMin,
      vehicleCapacity: el.route.vehicleCapacity,
      bikeCapacity: el.route.bikeCapacity,
      wheelchairCapacity: el.route.wheelchairCapacity,
      pathType,
      isVisible: false,
      isSelected: false,
      vehicle: el.route.vehicle,
      vehicleId: el.route.vehicle.id,
      serviceCompanyId: el.route.serviceCompanyId,
      workgroupId: el.route.workgroupId,
      accountingCenterId: el.route.accountingCenterId,
      stops: el.route[pathType]?.stationIds ? el.route[pathType].stationIds.length : 0,
      stations,
      hours: stations.map((item) => timeLocalFormat(item.expectedDepartureHour || item.expectedArrivalHour)),
      stationsIds: el.route[pathType]?.stationIds || {},
      usedStationIds: el.route[pathType]?.usedStationIds || [],
      secondaryDrivers: el.route.secondaryDrivers,
      isVisibleForEnabledRidersByAdmin: el.route.isVisibleForEnabledRidersByAdmin,
      routeShifts: el.route.routeShifts,
    };
  });
}

function mergeDraftRoutes(routes, draftRoutes) {
  return routes.map((route) => {
    const draft = draftRoutes[route.id];
    if (!draft) return route;
    const track = 'path'; // multiple edit currently does not support return path
    const { encodedPolyline } = draft.draftVersion.routeDetail[track];
    if (!encodedPolyline) console.warn(`no polyline for route: ${route.id}`);
    const stations = orderStations(draft.draftVersion.routeDetail.path.lstRouteStopIds, draft.stations);

    // Decode polyline and validate coordinates
    let position = [];
    if (encodedPolyline) {
      const decoded = decodePolyline(encodedPolyline);
      // Filter out invalid coordinates (non-numeric lat/lng)
      position = decoded.filter(
        (coord) =>
          coord &&
          typeof coord.lat === 'number' &&
          typeof coord.lng === 'number' &&
          Number.isFinite(coord.lat) &&
          Number.isFinite(coord.lng),
      );
    }

    return {
      ...route,
      draftId: draft?.draftVersion?.id,
      distanceInKm: draft ? draft.draftVersion.routeDetail[track]?.distanceInKm : route.totalDistance,
      durationInMin: draft ? draft.draftVersion.routeDetail[track]?.durationInMin : route.totalDuration,
      stopIds: draft.draftVersion.routeDetail[track]?.lstRouteStopIds,
      stations,
      position,
    };
  });
}

export default {
  state: {
    allRoutes: [],
    allStations: [],
    draftRoutes: {},
    personnelMap: {}, // Map or Object?,
    instancePersonnel: [],
    modifiedRoutes: [],
    modifiedRoutesPersonnel: [],
    // Tracks forward-station id changes from marker moves: { [routeId]: { [oldId]: newId } }.
    // Used by syncStationsToReturnPath to resolve stale counterStopId links on the return path.
    stationIdMappings: {},
    // Tracks forward-station ids that are genuinely new to a route's forward path (added via
    // ADD_STATION_TO_ROUTE or reassigned via assignStationToRoute), as opposed to moved
    // (which stationIdMappings covers): { [routeId]: { [stationId]: true } }.
    // Used by syncStationsToReturnPath so these stops get a return counterpart created
    // instead of being skipped as "unmoved, never had one".
    newForwardStationIds: {},
    // Tracks which leg a rider's stop assignment was last edited on:
    // { [routeId]: { [personnelId]: 'TO' | 'FROM' } }. Used by syncStationsToReturnPath so an
    // explicit return-leg assignment isn't overwritten by mirroring the rider's forward stop.
    modifiedRiderLegs: {},
    currentWorkgroup: {},
    optSelectedUnassigned: [],
    optSelectedRoutes: [],
    routeTypeFilter: 'active',
    isEditMode: false,
    isOptimizationSelectionMode: false,
    isDrawingMode: false,
    optimizeWaypoints: true,
    shuttleRoutesCount: 0,
  },

  mutations: {
    SET_ALL_ROUTES(state, payload) {
      // Preserve visibility state from existing routes
      const previousVisibility = new Map(state.allRoutes.map((r) => [r.id, r.isVisible]));

      state.allStations = payload.routes;

      // Process routes first without assigning to state
      let newRoutes;
      if (payload.routeDrafts && Object.keys(payload.routeDrafts).length) {
        const { routeDrafts, routes } = payload;
        newRoutes = routesFromActive(routes);

        state.draftRoutes = {
          ...state.draftRoutes,
          ...routeDrafts,
        };
      } else {
        console.warn('this usage is deprecated, use SET_ALL_ROUTES_WITH_DRAFTS instead');
        newRoutes = routesFromActive(payload?.routes);
      }

      // Restore visibility and process ghost routes BEFORE assigning to state
      newRoutes.forEach((r) => {
        // Restore visibility state if route existed before
        if (previousVisibility.has(r.id)) {
          r.isVisible = previousVisibility.get(r.id);
        }

        if (r.isGhost) {
          if (state.draftRoutes[r.id]?.draftVersion?.routeDetail?.path?.encodedPolyline)
            r.position = decodePolyline(state.draftRoutes[r.id]?.draftVersion?.routeDetail?.path?.encodedPolyline);
          r.stations = orderStations(
            state.draftRoutes[r.id]?.draftVersion.routeDetail.path.lstRouteStopIds,
            state.draftRoutes[r.id]?.stations,
          );
          r.stationIds = state.draftRoutes[r.id]?.draftVersion.routeDetail.path.lstRouteStopIds;
        }
      });

      state.allRoutes = newRoutes;
    },
    TOGGLE_VISIBILITY(state, routeId) {
      const routeIndex = state.allRoutes.findIndex((el) => el.id === routeId);
      if (routeIndex > -1) {
        state.allRoutes[routeIndex].isVisible = !state.allRoutes[routeIndex].isVisible;
      }
    },
    SET_VISIBILITY_FOR_ALL_ROUTES(state, isVisible) {
      state.allRoutes.forEach((r) => {
        r.isVisible = isVisible;
      });
    },
    TOGGLE_SELECTED(state, routeId) {
      const routeIndex = state.allRoutes.findIndex((el) => el.id === routeId);
      if (routeIndex > -1) {
        state.allRoutes[routeIndex].isSelected = !state.allRoutes[routeIndex].isSelected;
      }
    },
    SELECT_ROUTE(state, routeId) {
      const routeIndex = state.allRoutes.findIndex((el) => el.id === routeId);
      if (routeIndex > -1) {
        state.allRoutes[routeIndex].isSelected = true;
      }
    },
    UNSELECT_ROUTE(state, routeId) {
      const routeIndex = state.allRoutes.findIndex((el) => el.id === routeId);
      if (routeIndex > -1) {
        state.allRoutes[routeIndex].isSelected = false;
      }
    },
    SET_INSTANCE_PERSONNELS(state, payload) {
      state.instancePersonnel = payload;
    },
    SET_PERSONNEL_MAP(state, personnels) {
      const p = {};
      personnels.forEach((el) => {
        p[el.personnel.id] = el;
      });
      state.personnelMap = p;
    },
    ADD_MODIFIED_ROUTE(state, routeId) {
      const ids = new Set(state.modifiedRoutes);
      ids.add(routeId);
      state.modifiedRoutes = [...ids];
    },
    REMOVE_MODIFIED_ROUTE(state, routeId) {
      removeFromArray(state.modifiedRoutes, routeId);
    },
    SET_MODIFIED_ROUTES(state, payload) {
      state.modifiedRoutes = payload;
    },
    UPDATE_DRAFT_RETURN_PATH(state, { routeId, returnPath, returnPathPersonnelStations, stations: newStations }) {
      const draft = state.draftRoutes[routeId];
      if (!draft?.draftVersion?.routeDetail) return;

      draft.draftVersion.routeDetail.returnPath = {
        ...draft.draftVersion.routeDetail.returnPath,
        ...returnPath,
      };

      if (returnPathPersonnelStations) {
        draft.draftVersion.routeDetail.returnPathPersonnelStations = returnPathPersonnelStations;
      }

      if (newStations?.length) {
        if (!draft.stations) draft.stations = [];
        newStations.forEach((s) => {
          const existingIndex = draft.stations.findIndex((existing) => existing.id === s.id);
          if (existingIndex === -1) {
            draft.stations.push(s);
          } else {
            // A reused return station comes back carrying riders merged from its forward
            // counterpart, so it has to replace the stale copy rather than be skipped.
            draft.stations.splice(existingIndex, 1, s);
          }
        });
      }

      // Prune orphaned stations no longer referenced by either forward or return path
      const forwardStopIds = new Set(draft.draftVersion.routeDetail.path?.lstRouteStopIds || []);
      const returnStopIds = new Set(draft.draftVersion.routeDetail.returnPath?.lstRouteStopIds || []);
      if (draft.stations) {
        draft.stations = draft.stations.filter((s) => forwardStopIds.has(s.id) || returnStopIds.has(s.id));
      }

      draft.draftVersion.updateTime = new Date().getTime();
    },

    RESTORE_RETURN_PATHS(state, returnPathSnapshot) {
      Object.entries(returnPathSnapshot).forEach(([routeId, { returnPath, returnPathPersonnelStations }]) => {
        const draft = state.draftRoutes[routeId];
        if (draft?.draftVersion?.routeDetail) {
          draft.draftVersion.routeDetail.returnPath = returnPath;
          if (returnPathPersonnelStations) {
            draft.draftVersion.routeDetail.returnPathPersonnelStations = returnPathPersonnelStations;
          }
        }
      });
    },
    SET_MODIFIED_ROUTES_PERSONNEL(state, payload) {
      state.modifiedRoutesPersonnel = payload;
    },
    SET_MAIN_CAMPUS(state, payload) {
      state.mainCampus = payload;
    },
    SET_CURRENT_WORKGROUP(state, payload) {
      // Create a clean copy to avoid circular references and excessive data
      const cleanPayload = {
        instance: payload.instance
          ? {
              id: payload.instance.id,
              name: payload.instance.name,
              workgroupType: payload.instance.workgroupType,
              // Only include essential properties to avoid large data
              ...Object.fromEntries(
                Object.entries(payload.instance).filter(
                  ([key, value]) => typeof value !== 'object' || value === null || Array.isArray(value),
                ),
              ),
            }
          : null,
        template: payload.template
          ? {
              ...payload.template,
              // Remove any potentially large nested objects
              personnel: undefined,
              routes: undefined,
              stations: undefined,
            }
          : null,
      };

      state.currentWorkgroup = cleanPayload;
    },
    ADD_TO_OPT_LIST(state, payload) {
      const { list, id } = payload;
      if (!state[list].includes(id)) state[list].push(id);
    },
    REMOVE_FROM_OPT_LIST(state, payload) {
      const { list, id } = payload;
      state[list] = removeFromArray(state[list], id);
    },
    ADD_STATION_TO_ROUTE(state, payload) {
      const { routeId, station } = payload;

      // Ensure the draft route exists
      if (!state.draftRoutes[routeId]) {
        console.error(`ADD_STATION_TO_ROUTE: Draft route ${routeId} does not exist`);
        return;
      }

      if (station.personnels === undefined) station.personnels = [];
      if (station.personnelIds === undefined) station.personnelIds = [];
      if (!state.draftRoutes[routeId].stations) state.draftRoutes[routeId].stations = [];
      state.draftRoutes[routeId].stations.push(station);
      state.draftRoutes[routeId].draftVersion.routeDetail.path.lstRouteStopIds.push(station.id);

      // Track this id as genuinely new to the route's forward path (not a marker move), so
      // syncStationsToReturnPath knows to create a return counterpart rather than skip it.
      if (!state.newForwardStationIds[routeId]) {
        Vue.set(state.newForwardStationIds, routeId, {});
      }
      Vue.set(state.newForwardStationIds[routeId], station.id, true);
    },
    ADD_DRAFT_ROUTE(state, payload) {
      const { id, draft } = payload;
      // Object spread used for keeping vue reactivity intact
      state.draftRoutes = {
        ...state.draftRoutes,
        [id]: draft,
      };
    },
    UPDATE_DRAFT_STATION(state, payload) {
      const { routeId, stationId, oldStationId, location } = payload;

      // Look up the station by oldStationId rather than trusting a positional stationIndex
      // computed before an async API call — the stations array may have been reordered/pruned
      // (e.g. by syncStationsToReturnPath) in the meantime, which would invalidate a stale index.
      const stationIdx = state.draftRoutes[routeId].stations.findIndex((s) => s.id === oldStationId);

      if (stationIdx !== -1) {
        const sidx = state.draftRoutes[routeId].draftVersion.routeDetail.path.lstRouteStopIds.findIndex(
          (sid) => sid === oldStationId,
        );
        state.draftRoutes[routeId].draftVersion.routeDetail.path.lstRouteStopIds[sidx] = stationId;
        state.draftRoutes[routeId].stations[stationIdx].id = stationId;
        state.draftRoutes[routeId].stations[stationIdx].location = location;
      } else {
        state.draftRoutes[routeId].stations.push({ location, id: stationId });
      }

      // Record the old→new id mapping so syncStationsToReturnPath can resolve stale
      // counterStopId links on the return path (return stations still point to oldId).
      if (stationId && oldStationId && stationId !== oldStationId) {
        if (!state.stationIdMappings[routeId]) {
          Vue.set(state.stationIdMappings, routeId, {});
        }
        Vue.set(state.stationIdMappings[routeId], oldStationId, stationId);
      }
    },
    CLEAR_STATION_ID_MAPPINGS(state, routeId = null) {
      if (routeId) {
        Vue.set(state.stationIdMappings, routeId, {});
        Vue.set(state.newForwardStationIds, routeId, {});
      } else {
        Vue.set(state, 'stationIdMappings', {});
        Vue.set(state, 'newForwardStationIds', {});
      }
    },
    // Kept separate from CLEAR_STATION_ID_MAPPINGS because the lifecycles differ: station
    // tracking is spent once the sync has rewritten the draft, but a rider's leg marker has to
    // outlive that and survive until the draft is actually persisted. Clearing it any earlier
    // would let a retry after a failed save mirror the forward stop back over the rider's
    // deliberate return-leg choice.
    CLEAR_MODIFIED_RIDER_LEGS(state, routeId = null) {
      if (routeId) {
        Vue.set(state.modifiedRiderLegs, routeId, {});
      } else {
        Vue.set(state, 'modifiedRiderLegs', {});
      }
    },
    // Track a forward-station id reassigned onto routeId (e.g. via assignStationToRoute) as
    // genuinely new to that route's forward path — see newForwardStationIds above.
    TRACK_NEW_FORWARD_STATION_ID(state, { routeId, stationId }) {
      if (!state.newForwardStationIds[routeId]) {
        Vue.set(state.newForwardStationIds, routeId, {});
      }
      Vue.set(state.newForwardStationIds[routeId], stationId, true);
    },
    SET_ROUTE_TYPE_FILTER(state, payload) {
      state.routeTypeFilter = payload;
    },
    SET_EDIT_MODE(state, payload) {
      state.isEditMode = payload;
    },
    SET_OPTIMIZATION_SELECTION_MODE(state, payload) {
      state.isOptimizationSelectionMode = payload;
    },
    UPDATE_DRAFT_ROUTE_LINE(state, payload) {
      const { routeId, encodedPolyline, distance, duration } = payload;

      // const lstTrackPositions = path.map((c) => ({ latitude: c.lat, longitude: c.lng }));
      const distanceInKm = distance / 1000;
      const durationInMin = duration / 60;
      state.draftRoutes[routeId].draftVersion.routeDetail.path = {
        ...state.draftRoutes[routeId].draftVersion.routeDetail.path,
        encodedPolyline,
        distanceInKm,
        durationInMin,
      };
      state.draftRoutes[routeId].draftVersion.updateTime = new Date().getTime();
    },

    UPDATE_DRAFT_ROUTE_LINE_DURATION(state, payload) {
      const { routeId, duration } = payload;
      state.draftRoutes[routeId].draftVersion.routeDetail.path.durationInMin = duration / 60;
    },

    UPDATE_DRAFT_ROUTE_STATIONS(state, payload) {
      const { routeId, stations } = payload;
      state.draftRoutes[routeId].stations = stations;
      state.draftRoutes[routeId].draftVersion.routeDetail.path.lstRouteStopIds = stations.map((s) => s.id);
    },
    UPDATE_DRAFT_ROUTE_LEGS(state, payload) {
      const { routeId, legs } = payload;
      state.draftRoutes[routeId].draftVersion.routeDetail.path.legs = legs.map((l) => l?.duration?.value);
    },
    ASSIGN_PERSONNEL_TO_STOP_DRAFT(state, payload) {
      const { routeId, stationId, personnelId, currentRouteId, direction = 'TO' } = payload;

      // 'TO' is the forward leg, 'FROM' the return leg; each leg keeps its own personnel map.
      const personnelStationsKey = direction === 'TO' ? 'pathPersonnelStations' : 'returnPathPersonnelStations';

      const { routeDetail } = state.draftRoutes[routeId].draftVersion;
      if (!routeDetail[personnelStationsKey]) {
        // Vue 2 cannot track a property added by plain assignment, which would leave the
        // Vue.set below inert. Drafts routinely arrive without returnPathPersonnelStations.
        Vue.set(routeDetail, personnelStationsKey, {});
      }

      // A personnel belongs to a single route, so moving to a different route clears their
      // assignments on both legs of the old one. Within the same route only the leg being
      // reassigned is cleared, since each direction can be edited independently.
      // Ids can arrive as numbers or strings depending on the caller, so compare them as strings
      const isCrossRouteMove = currentRouteId != null && String(currentRouteId) !== String(routeId);
      const legKeysToClear = isCrossRouteMove
        ? ['pathPersonnelStations', 'returnPathPersonnelStations']
        : [personnelStationsKey];
      const currentRoute = currentRouteId && state.draftRoutes[currentRouteId];
      const currentRouteDetail = currentRoute?.draftVersion?.routeDetail;

      if (currentRouteDetail) {
        const filteredStations = (currentRoute.stations || []).filter((station) => station.routeId);

        legKeysToClear.forEach((legKey) => {
          const currentStationId = currentRouteDetail[legKey]?.[personnelId];
          if (!currentStationId) return;

          // 1. Remove from this leg's personnel-station mapping
          Vue.delete(currentRouteDetail[legKey], personnelId);

          // 2. Find the station in the stations array and remove personnel
          const station = filteredStations.find((s) => s.id === currentStationId);
          if (!station) return;

          // 3. Remove personnel ID from personnelIds array
          if (Array.isArray(station.personnelIds)) {
            const personnelIdIndex = station.personnelIds.indexOf(personnelId);
            if (personnelIdIndex !== -1) {
              station.personnelIds.splice(personnelIdIndex, 1);
            }
          }

          // 4. Remove personnel object from personnels array if it exists
          if (Array.isArray(station.personnels)) {
            const personnelIndex = station.personnels.findIndex((p) => getPersonnelId(p) === personnelId);
            if (personnelIndex !== -1) {
              station.personnels.splice(personnelIndex, 1);
            }
          }
        });
      }

      // Assign personnel to new station/route in this leg's personnel-station mapping
      Vue.set(routeDetail[personnelStationsKey], personnelId, stationId);

      // Remember which leg this was edited on. The return-path sync mirrors forward stops onto
      // their counterparts, which would otherwise silently undo a deliberate 'FROM' assignment.
      if (!state.modifiedRiderLegs[routeId]) {
        Vue.set(state.modifiedRiderLegs, routeId, {});
      }
      Vue.set(state.modifiedRiderLegs[routeId], personnelId, direction);
      if (isCrossRouteMove && state.modifiedRiderLegs[currentRouteId]) {
        Vue.delete(state.modifiedRiderLegs[currentRouteId], personnelId);
      }

      // Find the target station and add personnel to it
      const draftStations = state.draftRoutes[routeId].stations.filter((station) => station.routeId) || [];

      if (draftStations.length) {
        const targetStationIndex = draftStations.findIndex((station) => station.id === stationId);
        if (targetStationIndex !== -1) {
          const targetStation = draftStations[targetStationIndex];

          // Initialize arrays if they don't exist
          if (!targetStation.personnelIds) {
            Vue.set(targetStation, 'personnelIds', []);
          }

          if (!targetStation.personnels) {
            Vue.set(targetStation, 'personnels', []);
          }

          // Add to personnelIds if not already there
          if (Array.isArray(targetStation.personnelIds) && !targetStation.personnelIds.includes(personnelId)) {
            targetStation.personnelIds.push(personnelId);
          }

          // Add to personnels array if not already there
          const personnelExists = targetStation.personnels.some((p) => getPersonnelId(p) === personnelId);

          if (!personnelExists) {
            // Get personnel object from instancePersonnel
            const personnelObject = state.instancePersonnel.find((p) => getPersonnelId(p) === personnelId);
            if (personnelObject) {
              targetStation.personnels.push(cloneObject(personnelObject));
            }
          }
        }
      }

      // Update instancePersonnel array
      const targetPersonnelIndex = state.instancePersonnel.findIndex((el) => el.personnel.id === personnelId);
      if (targetPersonnelIndex === -1) return;
      const clonedPersonnel = cloneObject(state.instancePersonnel[targetPersonnelIndex]);
      // Update station assignment on the leg being assigned
      const instanceStationKey = direction === 'TO' ? 'station' : 'returnStation';
      if (!clonedPersonnel[instanceStationKey]) {
        clonedPersonnel[instanceStationKey] = {};
      }
      clonedPersonnel[instanceStationKey].id = stationId;
      // The opposite leg's stop belonged to the old route, so it no longer applies
      if (isCrossRouteMove) {
        clonedPersonnel[direction === 'TO' ? 'returnStation' : 'station'] = {};
      }
      // Update the route of the newly assigned personnel
      const newRoute = this.getters.getRouteById(routeId);
      clonedPersonnel.route = newRoute;

      state.instancePersonnel.splice(targetPersonnelIndex, 1, clonedPersonnel);

      // Track modified routes for personnel assignments (separate from path modifications)
      const modifiedIds = new Set(state.modifiedRoutesPersonnel);
      modifiedIds.add(routeId);
      if (currentRouteId && currentRouteId !== routeId) {
        modifiedIds.add(currentRouteId);
      }
      state.modifiedRoutesPersonnel = [...modifiedIds];
    },
    SET_OPTIMIZE_WAYPOINTS_CHECK(state, value) {
      state.optimizeWaypoints = value;
    },
    TOGGLE_DRAWING_MODE(state) {
      state.isDrawingMode = !state.isDrawingMode;
    },
    SET_DRAWING_MODE(state, payload) {
      state.isDrawingMode = payload;
    },
    ADD_NEW_ROUTE(state, route) {
      const pathType = 'routePath';
      // const lastColor = state.allRoutes[state.allRoutes.length - 1].routeColor;
      state.allRoutes.push({
        ...route,
        driver: `${route.driver.name ? route.driver.name : ''} ${route.driver.surname ? route.driver.surname : ''}`,
        position: route[pathType]?.pathCoordinates
          ? route[pathType].pathCoordinates.map((item) => ({
              lat: item[0],
              lng: item[1],
            }))
          : [],
        isGhost: true,
        isVisible: false,
        isSelected: false,
        routeColor: distinctColors[(state.allRoutes.length + 1) % distinctColors.length],
        endTime: intToTimeStr(route.endTime),
        startTime: intToTimeStr(route.startTime),
        stations: [],
        stationIds: [],
      });
    },
    RESET_OPT_SELECTED(state, payload) {
      state[payload.list] = [];
    },
    SET_SHUTTLE_ROUTES_COUNT(state, count) {
      state.shuttleRoutesCount = count;
    },
  },

  actions: {
    setShuttleRoutesCount({ commit }, count) {
      commit('SET_SHUTTLE_ROUTES_COUNT', count);
    },

    async fetchWorkgroupData({ commit, dispatch }, workgroupId) {
      try {
        const result = await workgroupService.getInstance(workgroupId);

        try {
          commit('SET_CURRENT_WORKGROUP', {
            instance: result.instance,
            template: result.template,
          });
        } catch (commitError) {
          console.error('Error committing workgroup data:', commitError);
          // Try with minimal data
          commit('SET_CURRENT_WORKGROUP', {
            instance: { id: result.instance?.id, name: result.instance?.name },
            template: { id: result.template?.id },
          });
        }

        if (result.personnel && Array.isArray(result.personnel)) {
          try {
            // Use centralized action to update personnel data in both modules
            await dispatch('routePersonnel/updateInstancePersonnel', result.personnel, { root: true });
          } catch (personnelError) {
            console.error('Error updating personnel data:', personnelError);
          }
        }

        return result;
      } catch (error) {
        console.error('Error fetching workgroup data:', error);
        throw error;
      }
    },
  },

  getters: {
    isRing(state) {
      return state.currentWorkgroup?.template?.direction === 'RING';
    },
    isRoundTrip(state) {
      return state.currentWorkgroup?.template?.direction === 'ROUND_TRIP';
    },
    toType(state) {
      // "CAMPUS", "PERSONNEL_HOME_ADDRESS"
      return state.currentWorkgroup?.template?.toType || '';
    },
    isCurrentTabInbound(state, getters, rootState) {
      const isIncoming = state.currentWorkgroup?.template?.toType === 'CAMPUS';
      const { isRouteToggleOn } = rootState.routeEdit;
      return (isIncoming && isRouteToggleOn) || (!isIncoming && !isRouteToggleOn);
    },
    routeListMap(state) {
      return new Map(state.allRoutes.map((el) => [el.id, el]));
    },
    getRouteById: (state) => (routeId) => state.allRoutes.find((route) => route.id === routeId),
    activeRoutes(state) {
      return state.allRoutes.filter((el) => el.isActive);
    },
    passiveRoutes(state) {
      return state.allRoutes.filter((el) => !el.isActive);
    },
    routeListSelected(state) {
      return state.allRoutes.filter((el) => el.isSelected);
    },
    allRoutesMerged(state, getters) {
      const routes = state.routeTypeFilter === 'passive' ? getters.passiveRoutes : getters.activeRoutes;
      if (state.isEditMode) {
        return mergeDraftRoutes(routes, state.draftRoutes, state.personnelMap);
      }
      return routes;
    },
    routeListVisible(state, getters) {
      if (state.currentWorkgroup?.template?.direction === 'RING') {
        return getters.allRoutesMerged.slice(0, 1);
      }
      return getters.allRoutesMerged.filter((el) => el.isVisible);
    },
    personnelByStation(state) {
      const routes = {};
      state.allRoutes.forEach((route) => {
        if (!routes[route.id]) routes[route.id] = {};
        if (!route.stations) route.stations = [];
        route.stations.forEach((station) => {
          if (station && station.personnelIds) {
            if (!routes[route.id][station.id]) routes[route.id][station.id] = [];
            station.personnelIds.forEach((id) => {
              routes[route.id][station.id].push(state.personnelMap[id]);
            });
          }
        });
      });
      return routes;
    },
    personnelByRoute(state) {
      const routes = {};
      state.instancePersonnel.forEach((p) => {
        routes[p.route.id] = routes[p.route.id] || [];
        routes[p.route.id].push(p);
      });
      return routes;
    },
    personnelWithStationOnDraft(state) {
      const routeIds = Object.keys(state.draftRoutes);
      return routeIds
        .map((rid) => Object.keys(state.draftRoutes[rid].draftVersion.routeDetail.pathPersonnelStations))
        .flat();
    },
    routeListModifiedRoutes(state) {
      return state.modifiedRoutes;
    },
    currentWorkgroup(state) {
      return state.currentWorkgroup;
    },
    workgroupType(state) {
      return state.currentWorkgroup?.template?.workgroupType;
    },
    personnelWithoutRoute(state, getters, rootState) {
      if (!rootState?.routeList?.currentWorkgroup) return [];
      if (!getters.personnelByRoute?.null) return [];
      const { workgroupType } = rootState.routeList.currentWorkgroup.template;
      if (state.isEditMode) {
        const filteredIds = Object.keys(state.personnelMap).filter(
          (id) => !getters.personnelWithStationOnDraft.includes(id) && !state.personnelMap[id]?.route?.id,
        );
        return filteredIds.map((id) => state.personnelMap[id]);
      }
      if (workgroupType === 'SHUTTLE') return getters.personnelByRoute.null;
      return getters.personnelByRoute.null.filter((p) => !p.personnel.isVanpoolDriver);
    },
    // DEPRECATED use currentWorkgroupCampuses instead
    mainCampus(state, getters, rootState) {
      if (!state.currentWorkgroup?.template) return {};
      const { template } = state.currentWorkgroup;
      const where = template.fromType === 'CAMPUS' ? 'fromTerminalReferenceId' : 'toTerminalReferenceId';
      const campus = rootState.campuses.find((el) => el.id === template[where]);
      if (!campus) return {};
      return {
        id: campus.id,
        name: campus.name,
        title: campus.title,
        position: {
          lat: campus.location.latitude,
          lng: campus.location.longitude,
        },
      };
    },
    // THIS AND RELATED FIELDS NEED TO CHANGE IF THERE ARE CAMPUS TO CAMPUS WORKGROUPS
    currentWorkgroupCampuses(state, getters, rootState) {
      if (!state.currentWorkgroup?.template) return [];
      const { template } = state.currentWorkgroup;
      const where = template.fromType === 'CAMPUS' ? 'fromTerminal' : 'toTerminal';
      const lookup = template[`${where}References`].length
        ? new Set(template[`${where}References`].map((e) => e.destinationId))
        : new Set([template[`${where}ReferenceId`]]);

      // Ensure campuses array exists and is valid
      if (!Array.isArray(rootState.campuses) || !rootState.campuses.length) return [];

      const campuses = objectifyByKey([...rootState.campuses], 'id');

      // objectifyByKey returns undefined for empty arrays, so check for that
      if (!campuses) return [];

      return [...lookup]
        .filter((id) => campuses[id] && campuses[id].location) // Filter out undefined campuses and those without location
        .map((id) => ({
          ...campuses[id],
          position: {
            lat: campuses[id].location.latitude,
            lng: campuses[id].location.longitude,
          },
        }));
    },
    optSelectedPersonnel(state, getters) {
      return state.optSelectedRoutes.map((rid) => {
        const r = getters.routeListMap.get(rid);
        const personnelByRoute = getters.personnelByRoute[rid] || [];

        return {
          route: {
            id: r.id,
            name: r.name,
          },
          personnel: personnelByRoute.map((p) => ({
            id: p.personnel.id,
            name: p.personnel.name,
            lastName: p.personnel.surname,
          })),
        };
      });
    },
    optSelectedPersonnelWithoutRoute(state) {
      return state.optSelectedUnassigned
        .filter((pid) => state.personnelMap[pid]) // Filter out missing personnel
        .map((pid) => {
          const personnelData = state.personnelMap[pid];
          // Handle both direct and nested personnel structures
          const personnel = personnelData.personnel || personnelData;
          return {
            id: personnel.id || pid,
            name: personnel.name,
            lastName: personnel.surname,
          };
        });
    },
    optSelectedPersonnelCount(state, getters) {
      const p = getters.optSelectedPersonnel.reduce((pre, cur) => pre + cur.personnel.length, 0);
      const pNoRoute = getters.optSelectedPersonnelWithoutRoute.length;
      return p + pNoRoute;
    },
    allStations(state) {
      return state.allStations;
    },
    isSube(state, getters) {
      const campuses = getters.currentWorkgroupCampuses;
      const campusName = campuses.length ? campuses[0].name : '';
      const normalizedName = clearSearchString(campusName.toLowerCase());

      return normalizedName.includes('sube');
    },
    isVanpool(state) {
      return state.currentWorkgroup?.template?.workgroupType === 'VANPOOL';
    },
  },
};

/* eslint-enable no-param-reassign */
/* eslint-enable no-unused-vars */
